<template>
  <div class="page-wrapper">
    <HeaderComponent />

    <main class="main-content">
      <!-- Hero Section -->
      <section class="hero-section">
        <div class="hero-pattern"></div>
        <div class="hero-content">
          <div class="breadcrumb">
            <router-link to="/">Home</router-link>
            <span class="separator">/</span>
            <router-link to="/services">Services</router-link>
            <span class="separator">/</span>
            <router-link to="/liquor-license">Liquor License</router-link>
            <span class="separator">/</span>
            <span class="current">Track Application</span>
          </div>
          <h1 class="hero-title">Track Application</h1>
          <p class="hero-subtitle">Check the status of your liquor licence application</p>
        </div>
      </section>

      <!-- Main Content -->
      <section class="content-section">
        <div class="content-container">
          <div class="content-grid">
            <!-- Left Column - Search & Results -->
            <div class="main-column">
              <div class="card search-card">
                <div class="card-header">
                  <div class="card-icon">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                      <circle cx="11" cy="11" r="8"></circle>
                      <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
                    </svg>
                  </div>
                  <div>
                    <h2 class="card-title">Track Your Application</h2>
                    <p class="card-description">Enter your details to check application status</p>
                  </div>
                </div>

                <div class="card-body">
                  <!-- Search Form -->
                  <div class="search-form" v-if="!application">
                    <div class="form-group">
                      <label class="form-label">Search By</label>
                      <div class="search-type-tabs">
                        <button 
                          class="search-type-tab"
                          :class="{ active: searchType === 'businessId' }"
                          @click="searchType = 'businessId'"
                        >
                          Business ID
                        </button>
                        <button 
                          class="search-type-tab"
                          :class="{ active: searchType === 'billNo' }"
                          @click="searchType = 'billNo'"
                        >
                          Bill Number
                        </button>
                        <button 
                          class="search-type-tab"
                          :class="{ active: searchType === 'phone' }"
                          @click="searchType = 'phone'"
                        >
                          Phone Number
                        </button>
                      </div>
                    </div>

                    <div class="form-group">
                      <label class="form-label">{{ searchLabel }}</label>
                      <div class="search-input-wrapper">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                          <circle cx="11" cy="11" r="8"></circle>
                          <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
                        </svg>
                        <input 
                          type="text" 
                          v-model="searchKeyword"
                          :placeholder="searchPlaceholder"
                          @keyup.enter="searchApplication"
                          class="search-input"
                        />
                        <button v-if="searchKeyword" @click="searchKeyword = ''" class="clear-btn">
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                            <line x1="18" y1="6" x2="6" y2="18"></line>
                            <line x1="6" y1="6" x2="18" y2="18"></line>
                          </svg>
                        </button>
                      </div>
                    </div>

                    <button 
                      class="btn btn-primary btn-block"
                      @click="searchApplication"
                      :disabled="!searchKeyword || searching"
                    >
                      <span v-if="!searching">
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                          <circle cx="11" cy="11" r="8"></circle>
                          <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
                        </svg>
                        Track Application
                      </span>
                      <span v-else class="loading-text">
                        <span class="spinner-sm"></span>
                        Searching...
                      </span>
                    </button>
                  </div>

                  <!-- Loading State -->
                  <div v-if="searching" class="loading-state">
                    <div class="loader-spinner"></div>
                    <p>Searching for your application...</p>
                  </div>

                  <!-- No Results -->
                  <div v-if="noResults && !searching" class="no-results">
                    <div class="no-results-icon">
                      <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
                        <circle cx="11" cy="11" r="8"></circle>
                        <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
                        <line x1="8" y1="8" x2="14" y2="14"></line>
                        <line x1="14" y1="8" x2="8" y2="14"></line>
                      </svg>
                    </div>
                    <h3>No Application Found</h3>
                    <p>We couldn't find an application matching your search. Please check your details and try again.</p>
                    <router-link to="/liquor-license" class="btn btn-outline">
                      Apply for New Licence
                    </router-link>
                  </div>

                  <!-- Application Found - Details -->
                  <div v-if="application" class="application-details">
                    <div class="details-header">
                      <button class="back-link" @click="resetSearch">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                          <line x1="19" y1="12" x2="5" y2="12"></line>
                          <polyline points="12 19 5 12 12 5"></polyline>
                        </svg>
                        New Search
                      </button>
                      <span class="status-badge" :class="getStatusClass(application.stageStatus)">
                        {{ application.stageStatus || application.status }}
                      </span>
                    </div>

                    <!-- Business Info Card -->
                    <div class="info-card-section">
                      <div class="business-header">
                        <div class="business-icon">
                          <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
                            <path d="M8 22h8"></path>
                            <path d="M12 11v11"></path>
                            <path d="M5 3l5 7h4l5-7"></path>
                            <path d="M6.5 7h11"></path>
                          </svg>
                        </div>
                        <div class="business-info">
                          <h3>{{ application.businessName }}</h3>
                          <span class="business-id">Business ID: {{ application.businessID }}</span>
                        </div>
                      </div>

                      <div class="details-grid">
                        <div class="detail-item">
                          <label>Bill Number</label>
                          <span>{{ application.billNo }}</span>
                        </div>
                        <div class="detail-item">
                          <label>Applicant</label>
                          <span>{{ application.applicant || application.fullNames }}</span>
                        </div>
                        <div class="detail-item">
                          <label>Phone Number</label>
                          <span>{{ application.phoneNumber || application.ownerPhone }}</span>
                        </div>
                        <div class="detail-item">
                          <label>Sub County</label>
                          <span>{{ application.subCountyName }}</span>
                        </div>
                        <div class="detail-item">
                          <label>Ward</label>
                          <span>{{ application.wardName }}</span>
                        </div>
                        <div class="detail-item">
                          <label>Date Applied</label>
                          <span>{{ formatDate(application.dateCreated) }}</span>
                        </div>
                        <div class="detail-item full-width" v-if="application.licenceSubCategory">
                          <label>Licence Type</label>
                          <span>{{ application.licenceSubCategory }}</span>
                        </div>
                      </div>
                    </div>

                    <!-- Status Timeline -->
                    <div class="timeline-section">
                      <h4 class="section-title">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                          <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"></polyline>
                        </svg>
                        Application Progress
                      </h4>
                      
                      <div class="timeline">
                        <div class="timeline-item" :class="{ completed: isStageCompleted('submitted'), current: isCurrentStage('submitted') }">
                          <div class="timeline-marker">
                            <svg v-if="isStageCompleted('submitted')" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3">
                              <polyline points="20 6 9 17 4 12"></polyline>
                            </svg>
                            <span v-else>1</span>
                          </div>
                          <div class="timeline-content">
                            <h5>Application Submitted</h5>
                            <p>{{ formatDate(application.dateCreated) }}</p>
                          </div>
                        </div>

                        <div class="timeline-item" :class="{ completed: isStageCompleted('inspection'), current: isCurrentStage('inspection') }">
                          <div class="timeline-marker">
                            <svg v-if="isStageCompleted('inspection')" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3">
                              <polyline points="20 6 9 17 4 12"></polyline>
                            </svg>
                            <span v-else>2</span>
                          </div>
                          <div class="timeline-content">
                            <h5>Inspection</h5>
                            <p v-if="application.dateInspected">Inspected on {{ formatDate(application.dateInspected) }}</p>
                            <p v-else>Pending inspection</p>
                          </div>
                        </div>

                        <div class="timeline-item" :class="{ completed: isStageCompleted('payment'), current: isCurrentStage('payment') }">
                          <div class="timeline-marker">
                            <svg v-if="isStageCompleted('payment')" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3">
                              <polyline points="20 6 9 17 4 12"></polyline>
                            </svg>
                            <span v-else>3</span>
                          </div>
                          <div class="timeline-content">
                            <h5>Payment</h5>
                            <p v-if="parseFloat(application.licenceBillBalance) === 0">Paid KES {{ formatCurrency(application.licenceReceiptAmount) }}</p>
                            <p v-else-if="parseFloat(application.licenceDetailAmount) > 0">Balance: KES {{ formatCurrency(application.licenceBillBalance) }}</p>
                            <p v-else>Awaiting invoice</p>
                          </div>
                        </div>

                        <div class="timeline-item" :class="{ completed: isStageCompleted('approval'), current: isCurrentStage('approval') }">
                          <div class="timeline-marker">
                            <svg v-if="isStageCompleted('approval')" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3">
                              <polyline points="20 6 9 17 4 12"></polyline>
                            </svg>
                            <span v-else>4</span>
                          </div>
                          <div class="timeline-content">
                            <h5>Approval</h5>
                            <p v-if="application.approved === 'true'">Approved on {{ formatDate(application.approvalDate) }}</p>
                            <p v-else>Pending approval</p>
                          </div>
                        </div>

                        <div class="timeline-item" :class="{ completed: isStageCompleted('issued'), current: isCurrentStage('issued') }">
                          <div class="timeline-marker">
                            <svg v-if="isStageCompleted('issued')" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3">
                              <polyline points="20 6 9 17 4 12"></polyline>
                            </svg>
                            <span v-else>5</span>
                          </div>
                          <div class="timeline-content">
                            <h5>Licence Issued</h5>
                            <p v-if="application.printable === 'true'">Ready to print</p>
                            <p v-else>Pending issuance</p>
                          </div>
                        </div>
                      </div>
                    </div>

                    <!-- Comments/Remarks -->
                    <div class="remarks-section" v-if="application.comments || application.approvalRemarks">
                      <h4 class="section-title">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                          <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
                        </svg>
                        Remarks
                      </h4>
                      <div class="remarks-content">
                        <p v-if="application.approvalRemarks">{{ application.approvalRemarks }}</p>
                        <p v-else-if="application.comments">{{ application.comments }}</p>
                      </div>
                    </div>

                    <!-- Action Buttons -->
                    <div class="action-buttons">
                      <router-link 
                        v-if="application.printable === 'true' && application.approved === 'true'"
                        :to="'/print-liquor-license?businessID=' + application.businessID"
                        class="btn btn-primary"
                      >
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                          <polyline points="6 9 6 2 18 2 18 9"></polyline>
                          <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"></path>
                          <rect x="6" y="14" width="12" height="8"></rect>
                        </svg>
                        Print Licence
                      </router-link>
                      <button 
                        v-if="parseFloat(application.licenceBillBalance) > 0"
                        class="btn btn-success"
                        @click="goToPayment"
                      >
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                          <rect x="1" y="4" width="22" height="16" rx="2" ry="2"></rect>
                          <line x1="1" y1="10" x2="23" y2="10"></line>
                        </svg>
                        Pay Now
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Right Column - Info -->
            <div class="info-column">
              <!-- Quick Actions -->
              <div class="info-card quick-actions-card">
                <div class="info-card-header">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <rect x="3" y="3" width="7" height="7"></rect>
                    <rect x="14" y="3" width="7" height="7"></rect>
                    <rect x="14" y="14" width="7" height="7"></rect>
                    <rect x="3" y="14" width="7" height="7"></rect>
                  </svg>
                  <h3>Quick Actions</h3>
                </div>
                <div class="quick-actions-list">
                  <router-link to="/liquor-license" class="quick-action-item">
                    <div class="action-icon blue">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                        <polyline points="14 2 14 8 20 8"></polyline>
                        <line x1="12" y1="18" x2="12" y2="12"></line>
                        <line x1="9" y1="15" x2="15" y2="15"></line>
                      </svg>
                    </div>
                    <div class="action-content">
                      <span class="action-title">New Application</span>
                      <span class="action-desc">Apply for a new licence</span>
                    </div>
                  </router-link>
                  <router-link to="/print-liquor-license" class="quick-action-item">
                    <div class="action-icon green">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <polyline points="6 9 6 2 18 2 18 9"></polyline>
                        <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"></path>
                        <rect x="6" y="14" width="12" height="8"></rect>
                      </svg>
                    </div>
                    <div class="action-content">
                      <span class="action-title">Print Licence</span>
                      <span class="action-desc">Print approved licence</span>
                    </div>
                  </router-link>
                </div>
              </div>

              <!-- Status Guide -->
              <div class="info-card">
                <div class="info-card-header">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <circle cx="12" cy="12" r="10"></circle>
                    <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"></path>
                    <line x1="12" y1="17" x2="12.01" y2="17"></line>
                  </svg>
                  <h3>Status Guide</h3>
                </div>
                <div class="status-guide">
                  <div class="status-item">
                    <span class="status-dot pending"></span>
                    <div>
                      <strong>Pending</strong>
                      <p>Application is being processed</p>
                    </div>
                  </div>
                  <div class="status-item">
                    <span class="status-dot inspection"></span>
                    <div>
                      <strong>Inspection</strong>
                      <p>Site inspection scheduled</p>
                    </div>
                  </div>
                  <div class="status-item">
                    <span class="status-dot approved"></span>
                    <div>
                      <strong>Approved</strong>
                      <p>Application approved, ready to print</p>
                    </div>
                  </div>
                  <div class="status-item">
                    <span class="status-dot rejected"></span>
                    <div>
                      <strong>Rejected</strong>
                      <p>Application was not approved</p>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Help Card -->
              <div class="info-card help-card">
                <div class="info-card-header">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path>
                  </svg>
                  <h3>Need Help?</h3>
                </div>
                <p class="help-text">If you have questions about your application status, please contact our support team.</p>
                <a href="/contact" class="help-link">
                  Contact Support
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <line x1="5" y1="12" x2="19" y2="12"></line>
                    <polyline points="12 5 19 12 12 19"></polyline>
                  </svg>
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
    </main>

    <FooterComponent />
  </div>
</template>

<script>
import HeaderComponent from "@/components/HeaderComponent.vue";
import FooterComponent from "@/components/FooterComponent.vue";
import { execute, liquor } from "@/api";

export default {
  name: "TrackLiquorApplication",
  components: {
    HeaderComponent,
    FooterComponent
  },
  props: {
    businessID: {
      type: String,
      default: null
    },
    billNo: {
      type: String,
      default: null
    },
    phone: {
      type: String,
      default: null
    }
  },
  data() {
    return {
      searchType: 'businessId',
      searchKeyword: '',
      searching: false,
      noResults: false,
      application: null
    };
  },
  computed: {
    searchLabel() {
      switch (this.searchType) {
        case 'businessId': return 'Business ID';
        case 'billNo': return 'Bill Number';
        case 'phone': return 'Phone Number';
        default: return 'Search';
      }
    },
    searchPlaceholder() {
      switch (this.searchType) {
        case 'businessId': return 'Enter your Business ID (e.g. 910)';
        case 'billNo': return 'Enter your Bill Number (e.g. BLL...)';
        case 'phone': return 'Enter phone number (e.g. 0712345678)';
        default: return 'Enter search keyword...';
      }
    }
  },
  mounted() {
    this.checkUrlParams();
  },
  methods: {
    checkUrlParams() {
      // Check props first (from router)
      if (this.businessID) {
        this.searchType = 'businessId';
        this.searchKeyword = this.businessID;
        this.searchApplication();
        return;
      }
      
      if (this.billNo) {
        this.searchType = 'billNo';
        this.searchKeyword = this.billNo;
        this.searchApplication();
        return;
      }
      
      if (this.phone) {
        this.searchType = 'phone';
        this.searchKeyword = this.phone;
        this.searchApplication();
        return;
      }
      
      // Also check query params directly as fallback
      const query = this.$route.query;
      if (query.businessID || query.businessId || query.id) {
        this.searchType = 'businessId';
        this.searchKeyword = query.businessID || query.businessId || query.id;
        this.searchApplication();
        return;
      }
      
      if (query.billNo || query.bill) {
        this.searchType = 'billNo';
        this.searchKeyword = query.billNo || query.bill;
        this.searchApplication();
        return;
      }
      
      if (query.phone || query.phoneNumber) {
        this.searchType = 'phone';
        this.searchKeyword = query.phone || query.phoneNumber;
        this.searchApplication();
      }
    },

    formatDate(dateString) {
      if (!dateString) return 'N/A';
      try {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'short',
          day: 'numeric'
        });
      } catch (e) {
        return dateString;
      }
    },

    formatCurrency(amount) {
      if (!amount) return '0.00';
      return new Intl.NumberFormat('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(amount);
    },

    getStatusClass(status) {
      if (!status) return 'pending';
      const s = status.toLowerCase();
      if (s.includes('approved') || s.includes('approval')) return 'approved';
      if (s.includes('reject') || s.includes('denied')) return 'rejected';
      if (s.includes('inspect')) return 'inspection';
      if (s.includes('pending') || s.includes('submitted')) return 'pending';
      return 'pending';
    },

    isStageCompleted(stage) {
      if (!this.application) return false;
      
      const status = (this.application.stageStatus || this.application.status || '').toLowerCase();
      const approved = this.application.approved === 'true';
      const printable = this.application.printable === 'true';
      const inspected = !!this.application.dateInspected;
      const paid = parseFloat(this.application.licenceBillBalance) === 0 && parseFloat(this.application.licenceReceiptAmount) > 0;

      switch (stage) {
        case 'submitted':
          return true; // Always completed if we have an application
        case 'inspection':
          return inspected;
        case 'payment':
          return paid;
        case 'approval':
          return approved || status.includes('approved');
        case 'issued':
          return printable && approved;
        default:
          return false;
      }
    },

    isCurrentStage(stage) {
      if (!this.application) return false;
      
      const stages = ['submitted', 'inspection', 'payment', 'approval', 'issued'];
      const currentIndex = stages.findIndex(s => !this.isStageCompleted(s));
      
      if (currentIndex === -1) return stage === 'issued'; // All completed
      return stages[currentIndex] === stage;
    },

    searchApplication() {
      if (!this.searchKeyword) return;

      this.searching = true;
      this.noResults = false;
      this.application = null;

      const data = new FormData();
      data.append("function", "printLiquorPermit");
      
      // Send the appropriate parameter based on search type
      if (this.searchType === 'businessId') {
        data.append("businessID", this.searchKeyword.trim());
      } else if (this.searchType === 'billNo') {
        data.append("billNo", this.searchKeyword.trim());
      } else if (this.searchType === 'phone') {
        data.append("phoneNumber", this.searchKeyword.trim());
      }

      execute(data, liquor)
        .then((res) => {
          this.searching = false;
          if (res.data.success && res.data.data.permit) {
            this.application = res.data.data.permit;
          } else {
            this.noResults = true;
          }
        })
        .catch((e) => {
          this.searching = false;
          this.noResults = true;
          console.error(e.message);
        });
    },

    resetSearch() {
      this.application = null;
      this.noResults = false;
    },

    goToPayment() {
      if (!this.application) return;
      this.$router.push({
        path: '/liquor-license',
        query: { 
          businessID: this.application.businessID,
          pay: 'true'
        }
      });
    }
  }
};
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap');

.page-wrapper {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background: #f8fafc;
}

.main-content {
  flex: 1;
  padding-top: 72px;
}

/* Hero Section */
.hero-section {
  background: linear-gradient(135deg, #0f4c81 0%, #0a3459 100%);
  padding: 48px 24px;
  position: relative;
  overflow: hidden;
}

.hero-pattern {
  position: absolute;
  inset: 0;
  background-image: url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.05'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E");
}

.hero-content {
  max-width: 1200px;
  margin: 0 auto;
  position: relative;
  z-index: 1;
}

.breadcrumb {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 16px;
  font-size: 14px;
}

.breadcrumb a {
  color: rgba(255, 255, 255, 0.7);
  text-decoration: none;
  transition: color 0.2s;
}

.breadcrumb a:hover {
  color: white;
}

.breadcrumb .separator {
  color: rgba(255, 255, 255, 0.4);
}

.breadcrumb .current {
  color: white;
}

.hero-title {
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-size: 36px;
  font-weight: 800;
  color: white;
  margin: 0 0 8px 0;
}

.hero-subtitle {
  font-size: 16px;
  color: rgba(255, 255, 255, 0.8);
  margin: 0;
}

/* Content Section */
.content-section {
  padding: 48px 24px;
}

.content-container {
  max-width: 1200px;
  margin: 0 auto;
}

.content-grid {
  display: grid;
  grid-template-columns: 1fr 380px;
  gap: 32px;
}

@media (max-width: 1024px) {
  .content-grid {
    grid-template-columns: 1fr;
  }
}

/* Cards */
.card {
  background: white;
  border-radius: 16px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  overflow: hidden;
}

.card-header {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 24px;
  border-bottom: 1px solid #e2e8f0;
}

.card-icon {
  width: 48px;
  height: 48px;
  background: linear-gradient(135deg, #0f4c81 0%, #0a3459 100%);
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
}

.card-title {
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-size: 20px;
  font-weight: 700;
  color: #1e293b;
  margin: 0;
}

.card-description {
  font-size: 14px;
  color: #64748b;
  margin: 4px 0 0 0;
}

.card-body {
  padding: 24px;
}

/* Search Form */
.search-form {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.form-label {
  font-size: 14px;
  font-weight: 600;
  color: #374151;
}

.search-type-tabs {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.search-type-tab {
  padding: 10px 16px;
  border: 2px solid #e2e8f0;
  border-radius: 8px;
  background: white;
  font-size: 14px;
  font-weight: 500;
  color: #64748b;
  cursor: pointer;
  transition: all 0.2s;
}

.search-type-tab:hover {
  border-color: #0f4c81;
  color: #0f4c81;
}

.search-type-tab.active {
  background: #0f4c81;
  border-color: #0f4c81;
  color: white;
}

.search-input-wrapper {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  border: 2px solid #e2e8f0;
  border-radius: 12px;
  background: white;
  transition: all 0.2s;
}

.search-input-wrapper:focus-within {
  border-color: #0f4c81;
  box-shadow: 0 0 0 3px rgba(15, 76, 129, 0.1);
}

.search-input-wrapper svg {
  color: #94a3b8;
  flex-shrink: 0;
}

.search-input {
  flex: 1;
  border: none;
  outline: none;
  font-size: 15px;
  color: #1e293b;
  background: transparent;
}

.search-input::placeholder {
  color: #94a3b8;
}

.clear-btn {
  padding: 4px;
  border: none;
  background: #f1f5f9;
  border-radius: 6px;
  cursor: pointer;
  color: #64748b;
  display: flex;
  align-items: center;
  justify-content: center;
}

.clear-btn:hover {
  background: #e2e8f0;
  color: #374151;
}

/* Buttons */
.btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 14px 24px;
  border-radius: 10px;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  border: none;
  text-decoration: none;
}

.btn-primary {
  background: linear-gradient(135deg, #0f4c81 0%, #0a3459 100%);
  color: white;
}

.btn-primary:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 8px 16px rgba(15, 76, 129, 0.3);
}

.btn-primary:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.btn-success {
  background: linear-gradient(135deg, #059669 0%, #047857 100%);
  color: white;
}

.btn-success:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 16px rgba(5, 150, 105, 0.3);
}

.btn-outline {
  background: white;
  border: 2px solid #e2e8f0;
  color: #1e293b;
}

.btn-outline:hover {
  border-color: #0f4c81;
  color: #0f4c81;
}

.btn-block {
  width: 100%;
}

.loading-text {
  display: flex;
  align-items: center;
  gap: 8px;
}

.spinner-sm {
  width: 18px;
  height: 18px;
  border: 2px solid rgba(255,255,255,0.3);
  border-top-color: white;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

/* Loading State */
.loading-state {
  text-align: center;
  padding: 48px 24px;
}

.loader-spinner {
  width: 48px;
  height: 48px;
  border: 3px solid #e2e8f0;
  border-top-color: #0f4c81;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 0 auto 16px;
}

.loading-state p {
  color: #64748b;
  font-size: 15px;
}

/* No Results */
.no-results {
  text-align: center;
  padding: 48px 24px;
}

.no-results-icon {
  width: 80px;
  height: 80px;
  background: #fef3c7;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 20px;
  color: #d97706;
}

.no-results h3 {
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-size: 20px;
  font-weight: 700;
  color: #1e293b;
  margin: 0 0 8px 0;
}

.no-results p {
  color: #64748b;
  font-size: 15px;
  margin: 0 0 24px 0;
}

/* Application Details */
.application-details {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.details-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.back-link {
  display: flex;
  align-items: center;
  gap: 6px;
  color: #64748b;
  font-size: 14px;
  text-decoration: none;
  cursor: pointer;
  background: none;
  border: none;
}

.back-link:hover {
  color: #0f4c81;
}

.status-badge {
  padding: 6px 16px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
}

.status-badge.approved {
  background: #d1fae5;
  color: #059669;
}

.status-badge.pending {
  background: #fef3c7;
  color: #d97706;
}

.status-badge.inspection {
  background: #dbeafe;
  color: #2563eb;
}

.status-badge.rejected {
  background: #fee2e2;
  color: #dc2626;
}

/* Business Info Section */
.info-card-section {
  background: #f8fafc;
  border-radius: 12px;
  padding: 20px;
  border: 1px solid #e2e8f0;
}

.business-header {
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 20px;
  padding-bottom: 16px;
  border-bottom: 1px solid #e2e8f0;
}

.business-icon {
  width: 48px;
  height: 48px;
  background: linear-gradient(135deg, #0f4c81 0%, #0a3459 100%);
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
}

.business-info h3 {
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-size: 18px;
  font-weight: 700;
  color: #1e293b;
  margin: 0;
}

.business-id {
  font-size: 13px;
  color: #64748b;
}

.details-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
}

.detail-item {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.detail-item.full-width {
  grid-column: span 2;
}

.detail-item label {
  font-size: 12px;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.detail-item span {
  font-size: 14px;
  color: #1e293b;
  font-weight: 500;
}

/* Timeline Section */
.timeline-section {
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 20px;
}

.section-title {
  display: flex;
  align-items: center;
  gap: 10px;
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-size: 16px;
  font-weight: 700;
  color: #1e293b;
  margin: 0 0 20px 0;
}

.section-title svg {
  color: #0f4c81;
}

.timeline {
  position: relative;
  padding-left: 32px;
}

.timeline::before {
  content: '';
  position: absolute;
  left: 11px;
  top: 0;
  bottom: 0;
  width: 2px;
  background: #e2e8f0;
}

.timeline-item {
  position: relative;
  padding-bottom: 24px;
}

.timeline-item:last-child {
  padding-bottom: 0;
}

.timeline-marker {
  position: absolute;
  left: -32px;
  width: 24px;
  height: 24px;
  background: white;
  border: 2px solid #e2e8f0;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  font-weight: 600;
  color: #94a3b8;
}

.timeline-item.completed .timeline-marker {
  background: #059669;
  border-color: #059669;
  color: white;
}

.timeline-item.current .timeline-marker {
  background: #0f4c81;
  border-color: #0f4c81;
  color: white;
  animation: pulse 2s infinite;
}

@keyframes pulse {
  0%, 100% { box-shadow: 0 0 0 0 rgba(15, 76, 129, 0.4); }
  50% { box-shadow: 0 0 0 8px rgba(15, 76, 129, 0); }
}

.timeline-content h5 {
  font-size: 14px;
  font-weight: 600;
  color: #1e293b;
  margin: 0 0 4px 0;
}

.timeline-content p {
  font-size: 13px;
  color: #64748b;
  margin: 0;
}

.timeline-item.completed .timeline-content h5 {
  color: #059669;
}

/* Remarks Section */
.remarks-section {
  background: #fffbeb;
  border: 1px solid #fef3c7;
  border-radius: 12px;
  padding: 20px;
}

.remarks-content {
  font-size: 14px;
  color: #92400e;
  line-height: 1.6;
}

.remarks-content p {
  margin: 0;
}

/* Action Buttons */
.action-buttons {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
}

/* Info Column */
.info-column {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.info-card {
  background: white;
  border-radius: 16px;
  padding: 24px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.1);
}

.quick-actions-card {
  border: 2px solid #fef3c7;
  background: linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%);
}

.info-card-header {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 20px;
}

.info-card-header svg {
  color: #0f4c81;
}

.info-card-header h3 {
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-size: 16px;
  font-weight: 700;
  color: #1e293b;
  margin: 0;
}

.quick-actions-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.quick-action-item {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 14px;
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  text-decoration: none;
  transition: all 0.2s;
}

.quick-action-item:hover {
  border-color: #0f4c81;
  box-shadow: 0 4px 12px rgba(15, 76, 129, 0.1);
  transform: translateX(4px);
}

.action-icon {
  width: 40px;
  height: 40px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.action-icon.blue {
  background: #dbeafe;
  color: #2563eb;
}

.action-icon.green {
  background: #d1fae5;
  color: #059669;
}

.action-content {
  display: flex;
  flex-direction: column;
}

.action-title {
  font-size: 14px;
  font-weight: 600;
  color: #1e293b;
}

.action-desc {
  font-size: 12px;
  color: #64748b;
}

/* Status Guide */
.status-guide {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.status-item {
  display: flex;
  align-items: flex-start;
  gap: 12px;
}

.status-dot {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  margin-top: 4px;
  flex-shrink: 0;
}

.status-dot.pending {
  background: #fbbf24;
}

.status-dot.inspection {
  background: #3b82f6;
}

.status-dot.approved {
  background: #10b981;
}

.status-dot.rejected {
  background: #ef4444;
}

.status-item strong {
  font-size: 13px;
  color: #1e293b;
  display: block;
  margin-bottom: 2px;
}

.status-item p {
  font-size: 12px;
  color: #64748b;
  margin: 0;
}

/* Help Card */
.help-card {
  background: linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%);
  border: 1px solid #bfdbfe;
}

.help-text {
  font-size: 14px;
  color: #1e40af;
  margin: 0 0 16px 0;
  line-height: 1.5;
}

.help-link {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: #1d4ed8;
  font-size: 14px;
  font-weight: 600;
  text-decoration: none;
}

.help-link:hover {
  text-decoration: underline;
}

/* Responsive */
@media (max-width: 768px) {
  .container { padding: 0 12px; }

  /* Hero shrunk. */
  .hero-section { padding: 20px 0 40px !important; }
  .hero-title { font-size: 20px !important; margin-bottom: 4px; }
  .hero-subtitle { font-size: 13px; line-height: 1.4; }
  .breadcrumb,
  .breadcrumb-current { font-size: 12px; }
  .content-section { padding: 24px 0 32px; }

  /* Card chrome — the search form is the primary UI here. */
  .content-grid { gap: 16px; }
  .card,
  .search-card { border-radius: 12px; }
  .card-header { padding: 12px 14px; }
  .card-body { padding: 16px 14px !important; }
  .card-title { font-size: 15px; }
  .card-description { font-size: 12.5px; line-height: 1.4; }

  /* Search form fields. */
  .search-form .form-group { margin-bottom: 12px; }
  .form-label { font-size: 12.5px; margin-bottom: 4px; }
  .form-input,
  .form-select { font-size: 14px; padding: 10px 12px; min-height: 42px; }
  .search-btn,
  .btn-primary { padding: 12px 16px; font-size: 13.5px; }

  /* Application details block. */
  .application-details { padding: 14px !important; }
  .status-badge { font-size: 11px; padding: 4px 10px; }
  .business-info h3 { font-size: 15px; }
  .business-id { font-size: 12px; }

  .details-grid { grid-template-columns: 1fr; gap: 8px; }
  .detail-item { padding: 8px 12px; }
  .detail-item.full-width { grid-column: span 1; }
  .detail-item span:first-child,
  .detail-item .detail-label { font-size: 11.5px; }
  .detail-item span + span,
  .detail-item .detail-value { font-size: 13px; word-break: break-word; }

  /* Timeline — always the tallest thing on this page. Shrink circles + gaps. */
  .timeline-section { padding: 14px 0; }
  .timeline-section h4,
  .timeline-title { font-size: 14px; margin-bottom: 10px; }
  .timeline { gap: 10px; }
  .timeline-item { gap: 10px; padding: 8px 0; }
  .timeline-icon,
  .timeline-marker,
  .timeline-dot {
    width: 26px !important;
    height: 26px !important;
    font-size: 12px !important;
    flex-shrink: 0;
  }
  .timeline-connector,
  .timeline-line { min-height: 20px !important; }
  .timeline-content h5,
  .timeline-item .stage-name { font-size: 13px; margin-bottom: 2px; }
  .timeline-content p,
  .timeline-item .stage-desc,
  .timeline-item .stage-date { font-size: 11.5px; line-height: 1.4; }

  .action-buttons { flex-direction: column; gap: 8px; }
  .action-buttons .btn { width: 100%; padding: 12px 16px; font-size: 13.5px; }

  /* Empty state / not-found. */
  .empty-state,
  .not-found-state { padding: 20px 12px; text-align: center; }
  .empty-state h3,
  .not-found-state h3 { font-size: 15px; }
  .empty-state p,
  .not-found-state p { font-size: 12.5px; }
}

@media (max-width: 480px) {
  .container { padding: 0 10px; }
  .hero-title { font-size: 18px !important; }
  .card-body { padding: 14px 12px !important; }
  .form-input, .form-select { font-size: 13.5px; min-height: 40px; }
  .timeline-icon,
  .timeline-marker,
  .timeline-dot { width: 22px !important; height: 22px !important; font-size: 11px !important; }
  .business-info h3 { font-size: 14px; }
}
</style>
